import { DELAY_ESTIMATION_URL } from '../config.js';
import { fetchCache } from '../utils/fetchCache.js';
import { showInfo } from '../utils/UnifiedNotificationSystem.js';
import { getCurrentLanguage } from '../translations.js';
import {
  WAIT_FAR_POLL_MS,
  WAIT_MAX_MS,
  WAIT_NEAR_ETA_S,
  WAIT_NEAR_POLL_MS,
} from './waitForQueuedImage.js';
import { adoptWaitEstimate, remainingWaitSeconds } from './waitEstimate.js';

const COLD_START_COPY = {
  video: {
    key: 'video_container_starting',
    catalogs: ['create-video', 'my-gallery'],
    fallback: 'We\'re restarting the video machines, so this will take about an extra hour. Sorry for the delay!',
  },
  image: {
    key: 'image_container_starting',
    catalogs: ['create-video', 'my-gallery', 'create-common'],
    fallback: 'We\'re restarting the image machines, so this will take about an extra hour. Sorry for the delay!',
  },
};
const coldStartToasted = new Set();

const containerStartingMessage = (kind) => {
  const copy = COLD_START_COPY[kind] || COLD_START_COPY.video;
  const lang = getCurrentLanguage() || 'en';
  for (const name of copy.catalogs) {
    const localized = window.translations?.[lang]?.[name]?.[copy.key];
    if (localized) return localized;
    const english = window.translations?.en?.[name]?.[copy.key];
    if (english) return english;
  }
  return copy.fallback;
};

export const notifyVideoContainerStarting = (data, jobKey, kind = 'video') => {
  if (!data?.container_starting || jobKey == null || jobKey === '') return;
  const mediaKind = kind === 'image' ? 'image' : 'video';
  const key = `${mediaKind}:${jobKey}`;
  if (coldStartToasted.has(key)) return;
  coldStartToasted.add(key);
  showInfo(containerStartingMessage(mediaKind), { duration: 8000 });
};

const parseOutputKeyFromPendingUrl = (pendingUrl) => {
  if (typeof pendingUrl !== 'string') return null;
  const match = pendingUrl.match(
    /\/standalone-video\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})(?:[/?#]|$)/i
  );
  return match?.[1] ? match[1].toLowerCase() : null;
};

const fetchWaitState = ({ outputKey, pendingUrl } = {}) => {
  const body = {};
  if (outputKey) body.output_key = outputKey;
  if (pendingUrl) body.pending_url = pendingUrl;
  return fetchCache.fetchJSON(`${DELAY_ESTIMATION_URL}/estimate-video-duration`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body,
    skipCache: true,
  });
};

const delay = (ms, { signal } = {}) => new Promise((resolve, reject) => {
  if (signal?.aborted) {
    reject(new DOMException('Aborted', 'AbortError'));
    return;
  }
  const timer = setTimeout(resolve, ms);
  const handleAbort = () => {
    clearTimeout(timer);
    reject(new DOMException('Aborted', 'AbortError'));
  };
  signal?.addEventListener('abort', handleAbort, { once: true });
});

const waitUntilVisible = ({ signal } = {}) => new Promise((resolve, reject) => {
  if (typeof document === 'undefined' || !document.hidden) {
    resolve();
    return;
  }
  if (signal?.aborted) {
    reject(new DOMException('Aborted', 'AbortError'));
    return;
  }
  const handleVisibility = () => {
    if (document.hidden) return;
    document.removeEventListener('visibilitychange', handleVisibility);
    signal?.removeEventListener('abort', handleAbort);
    resolve();
  };
  const handleAbort = () => {
    document.removeEventListener('visibilitychange', handleVisibility);
    reject(new DOMException('Aborted', 'AbortError'));
  };
  document.addEventListener('visibilitychange', handleVisibility);
  signal?.addEventListener('abort', handleAbort, { once: true });
});

export const waitForQueuedVideo = async ({
  outputKey,
  pendingUrl,
  signal,
  onEstimate,
  requireHd = false,
  nearEtaSeconds = WAIT_NEAR_ETA_S,
  farPollMs = WAIT_FAR_POLL_MS,
  nearPollMs = WAIT_NEAR_POLL_MS,
  maxWaitMs = WAIT_MAX_MS,
} = {}) => {
  const resolvedOutputKey = outputKey || parseOutputKeyFromPendingUrl(pendingUrl);
  if (!resolvedOutputKey && !pendingUrl) throw new Error('Missing output key');
  const startedAt = Date.now();
  let waitEstimate = null;

  const nextPollMs = () => {
    const remaining = remainingWaitSeconds(waitEstimate);
    if (remaining == null) return farPollMs;
    if (remaining <= nearEtaSeconds) return nearPollMs;
    return farPollMs;
  };

  while (Date.now() - startedAt < maxWaitMs) {
    if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
    await waitUntilVisible({ signal });
    let data = null;
    try {
      data = await fetchWaitState({ outputKey: resolvedOutputKey, pendingUrl });
    } catch {
      await delay(nearPollMs, { signal });
      continue;
    }
    if (typeof onEstimate === 'function') onEstimate(data);
    if (data?.status === 'completed') {
      if (requireHd && !data.is_hd) throw new Error('HD upgrade failed');
      return resolvedOutputKey;
    }
    if (data?.status === 'failed') throw new Error('Video generation failed');
    if (data?.estimated_seconds >= 0) {
      waitEstimate = adoptWaitEstimate(waitEstimate, data);
    }
    await delay(nextPollMs(), { signal });
  }
  throw new Error('Video not ready after 10 minutes');
};
